import { useState, useEffect } from 'react'
import { designService } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

export default function Admin() {
  const [stats, setStats] = useState(null)
  const [designs, setDesigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')
  const [uploading, setUploading] = useState(false)
  const [uploadMessage, setUploadMessage] = useState(null)
  const [s3Images, setS3Images] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [showNameDialog, setShowNameDialog] = useState(false)
  const [fileNames, setFileNames] = useState({})
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useState(null)

  useEffect(() => {
    loadAdminData()
    loadS3Images()
  }, [filter])

  const loadAdminData = async () => {
    try {
      setLoading(true)
      const response = await designService.listDesigns(0, 100)
      const designList = Array.isArray(response.data) ? response.data : response.data?.data || []
      setDesigns(designList)

      const completed = designList.filter(d => d.status === 'completed').length
      const failed = designList.filter(d => d.status === 'failed').length
      const pending = designList.filter(d => d.status === 'pending').length

      setStats({
        total: designList.length,
        completed,
        failed,
        pending,
        success_rate: designList.length > 0 ? ((completed / designList.length) * 100).toFixed(1) : 0
      })
      setError(null)
    } catch (err) {
      setError('Failed to load admin data')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const loadS3Images = async () => {
    try {
      const response = await designService.listImages()
      const imageList = Array.isArray(response.data) ? response.data : response.data?.data || []
      setS3Images(imageList)
    } catch (err) {
      console.error('Error loading S3 images:', err)
    }
  }

  const processFiles = (files) => {
    const fileArray = Array.from(files || [])
    if (fileArray.length === 0) return

    if (fileArray.length === 1) {
      // Single upload - ask for name directly
      const nameWithoutExt = fileArray[0].name.split('.')[0]
      setFileNames({ [fileArray[0].name]: nameWithoutExt })
      setSelectedFiles(fileArray)
      setShowNameDialog(true)
    } else if (fileArray.length > 1) {
      // Bulk upload - show dialog to choose original or custom names
      setSelectedFiles(fileArray)
      setFileNames(
        fileArray.reduce((acc, f) => {
          acc[f.name] = f.name.split('.')[0]
          return acc
        }, {})
      )
      setShowNameDialog(true)
    }
  }

  const handleFileSelect = (e) => {
    processFiles(e.target.files)
    e.target.value = ''
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    processFiles(e.dataTransfer.files)
  }

  const handleNameChange = (originalName, newName) => {
    setFileNames(prev => ({
      ...prev,
      [originalName]: newName
    }))
  }

  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) {
      setUploadMessage({ type: 'error', text: '❌ No files selected' })
      return
    }

    try {
      setUploading(true)
      setUploadMessage(null)
      let successCount = 0
      let failedCount = 0

      for (const file of selectedFiles) {
        try {
          const customName = fileNames[file.name] || file.name.split('.')[0]
          const formData = new FormData()
          formData.append('file', file)
          formData.append('image_name', customName)

          const response = await designService.uploadImage(formData)
          if (response.data?.success === true) {
            successCount++
          } else {
            failedCount++
          }
        } catch (err) {
          failedCount++
          console.error('Error uploading file:', file.name, err)
        }
      }

      if (failedCount > 0) {
        setUploadMessage({
          type: 'error',
          text: `⚠️ Uploaded ${successCount}/${selectedFiles.length} image(s). ${failedCount} failed.`
        })
      } else {
        setUploadMessage({
          type: 'success',
          text: `✅ Successfully uploaded ${successCount} image(s)`
        })
      }

      setShowNameDialog(false)
      setSelectedFiles([])
      setFileNames({})
      setTimeout(loadS3Images, 1000)
    } catch (err) {
      setUploadMessage({
        type: 'error',
        text: `❌ Upload failed: ${err.message}`
      })
    } finally {
      setUploading(false)
    }
  }

  const filteredS3Images = s3Images.filter(img =>
    img.filename.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredDesigns = filter === 'all'
    ? designs
    : designs.filter(d => d.status === filter)

  if (loading) return <LoadingSpinner />

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <button className="refresh-btn" onClick={loadAdminData}>
          🔄 Refresh
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Upload Section */}
      <div className="image-upload-section">
        <h2>📸 Image Management</h2>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          <strong>Important:</strong> Image names are used to match product names during CSV processing. Choose names carefully!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
          {/* Upload Area */}
          <div>
            <h3 style={{ marginBottom: '1rem' }}>Upload Images</h3>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              style={{
                position: 'relative',
                borderRadius: '8px',
                backgroundColor: dragActive ? 'rgba(217, 13, 14, 0.1)' : 'transparent',
                transition: 'background-color 0.2s'
              }}
            >
              <label className="upload-label">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
                <div className="upload-box" style={{
                  borderColor: dragActive ? '#d90d0e' : '#ddd',
                  backgroundColor: dragActive ? '#fff9f9' : 'white',
                  transition: 'all 0.2s'
                }}>
                  {uploading ? (
                    <>
                      <p style={{ fontSize: '2rem' }}>⏳</p>
                      <p>Uploading...</p>
                    </>
                  ) : (
                    <>
                      <p style={{ fontSize: '2rem' }}>📁</p>
                      <p><strong>Click to select or drag and drop</strong></p>
                      <p style={{ fontSize: '0.9rem', color: '#999' }}>PNG, JPG, GIF, WebP</p>
                    </>
                  )}
                </div>
              </label>
            </div>

            {uploadMessage && (
              <div style={{
                padding: '1rem',
                marginTop: '1rem',
                backgroundColor: uploadMessage.type === 'success' ? '#e8f5e9' : '#ffebee',
                color: uploadMessage.type === 'success' ? '#2e7d32' : '#c62828',
                borderRadius: '4px',
                border: `1px solid ${uploadMessage.type === 'success' ? '#4caf50' : '#f44336'}`,
                fontSize: '0.95rem'
              }}>
                {uploadMessage.text}
              </div>
            )}
          </div>

          {/* Search & Gallery */}
          <div>
            <h3 style={{ marginBottom: '1rem' }}>Current Images ({s3Images.length})</h3>
            <input
              type="text"
              placeholder="Search images..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '1rem',
                fontSize: '0.95rem'
              }}
            />

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
              maxHeight: '450px',
              overflowY: 'auto',
              padding: '1rem',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}>
              {filteredS3Images.length === 0 ? (
                <p style={{ gridColumn: '1/-1', color: '#999', textAlign: 'center' }}>
                  {s3Images.length === 0 ? 'No images uploaded' : 'No matches found'}
                </p>
              ) : (
                filteredS3Images.map((img, idx) => (
                  <div key={idx} style={{
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    padding: '0.75rem',
                    textAlign: 'center',
                    border: '1px solid #ddd'
                  }}>
                    <img
                      src={img.url}
                      alt={img.filename}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '80px',
                        marginBottom: '0.5rem',
                        borderRadius: '2px'
                      }}
                    />
                    <p style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: '#333',
                      margin: 0,
                      wordBreak: 'break-word',
                      lineHeight: '1.3'
                    }}>
                      {img.filename}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Name Dialog */}
      {showNameDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>
              {selectedFiles.length === 1 ? 'Customize Image Name' : 'Customize Image Names'}
            </h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              These names will be used to match with product names in CSV files. <strong>Be specific!</strong>
            </p>

            {selectedFiles.length > 1 && (
              <div style={{ marginBottom: '1rem' }}>
                <button
                  onClick={() => {
                    setFileNames(selectedFiles.reduce((acc, f) => {
                      acc[f.name] = f.name.split('.')[0]
                      return acc
                    }, {}))
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    marginRight: '0.5rem',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Use Original Names
                </button>
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              {selectedFiles.map((file, idx) => (
                <div key={idx} style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600' }}>
                    {file.name}
                  </label>
                  <input
                    type="text"
                    value={fileNames[file.name] || ''}
                    onChange={(e) => handleNameChange(file.name, e.target.value)}
                    placeholder="e.g., bananas, cucumber"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNameDialog(false)}
                disabled={uploading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadFiles}
                disabled={uploading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#d90d0e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                {uploading ? '⏳ Uploading...' : '📤 Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Designs</h3>
            <p className="stat-value">{stats.total}</p>
            <p className="stat-label">All time</p>
          </div>

          <div className="stat-card success">
            <h3>Completed</h3>
            <p className="stat-value">{stats.completed}</p>
            <p className="stat-label">Successfully rendered</p>
          </div>

          <div className="stat-card error">
            <h3>Failed</h3>
            <p className="stat-value">{stats.failed}</p>
            <p className="stat-label">Rendering errors</p>
          </div>

          <div className="stat-card pending">
            <h3>Pending</h3>
            <p className="stat-value">{stats.pending}</p>
            <p className="stat-label">In progress</p>
          </div>

          <div className="stat-card">
            <h3>Success Rate</h3>
            <p className="stat-value">{stats.success_rate}%</p>
            <p className="stat-label">Completion ratio</p>
          </div>
        </div>
      )}

      {/* Designs Section */}
      <div className="designs-section">
        <h2>Recent Designs</h2>

        <div className="filter-tabs">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({designs.length})
          </button>
          <button
            className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Completed ({designs.filter(d => d.status === 'completed').length})
          </button>
          <button
            className={`filter-btn ${filter === 'failed' ? 'active' : ''}`}
            onClick={() => setFilter('failed')}
          >
            Failed ({designs.filter(d => d.status === 'failed').length})
          </button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending ({designs.filter(d => d.status === 'pending').length})
          </button>
        </div>

        {filteredDesigns.length === 0 ? (
          <div className="empty-state">
            <p>No designs in this category</p>
          </div>
        ) : (
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Preview</th>
                </tr>
              </thead>
              <tbody>
                {filteredDesigns.map(design => (
                  <tr key={design.id}>
                    <td>#{design.id}</td>
                    <td>{design.item1_name}</td>
                    <td>
                      <span className="badge">
                        {design.template_type === 'single' ? '1️⃣' : '2️⃣'} {design.template_type}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${design.status}`}>
                        {design.status}
                      </span>
                    </td>
                    <td>{new Date(design.created_at).toLocaleDateString()}</td>
                    <td>
                      {design.rendered_image_url ? (
                        <a href={design.rendered_image_url} target="_blank" rel="noopener noreferrer" className="preview-link">
                          View
                        </a>
                      ) : (
                        <span className="no-preview">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
